<template>
  <div>
    <v-row
      style="padding: 10px"
      align="center"
      dense
    >
      <v-col>
        {{ $t("player_profile.analyze.label") }}
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-for="aggregation in matchReferenceAggregations"
          :key="aggregation.value"
          small
          class="mx-1 rounded-xl text-none"
          :outlined="selectedMatchReferenceAggregation.value !== aggregation.value"
          :color="selectedMatchReferenceAggregation.value === aggregation.value ? 'primary' : ''"
          :text="selectedMatchReferenceAggregation.value !== aggregation.value"
          @click="selectedMatchReferenceAggregation = aggregation"
        >
          {{ aggregation.text }}
        </v-btn>
      </v-col>
    </v-row>
    <performance-player-info-card
      style="padding-left: 10px; padding-right: 10px;"
      :player="player"
      :team="team"
      :event="event"
      :reference-stats="playerMatchReferenceStats"
      @update="$emit('update')"
    />
    <grid-layout
      v-if="layout"
      :key="layout.id"
      :layout="grid"
      :col-num="12"
      :row-height="70"
      :is-draggable="false"
      :is-resizable="false"
      :vertical-compact="true"
      :margin="[10, 10]"
      :use-css-transforms="true"
      @layout-ready="layoutReadyEvent()"
    >
      <grid-item
        v-for="item in grid"
        :key="item.i"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
      >
        <div
          v-if="layoutReady"
          class="fill-height"
        >
          <div
            v-if="item.type === 'card'"
            class="fill-height"
          >
            <performance-stat-card
              :large="item.h > 1"
              :loading="event.loading || exerciseStats.loading || matchReferenceStats.loading"
              :variable-key="item.key"
              :value="playerStats[item.key]"
              :max="performanceMaxValues[item.key] || event.maxValues[item.key]"
              :reversed="reversedVariables().includes(item.key)"
              :title="getVariableName(item.key)"
            />
          </div>
          <div
            v-else-if="item.type === 'chart'"
            class="fill-height"
          >
            <performance-graph-individual
              class="fill-height"
              :stats="playerStats"
              :chart="item"
              :variables="variables"
              :event="event"
              :player="player"
            />
          </div>
        </div>
      </grid-item>
    </grid-layout>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { EventApiService } from '@/data/services'
import {
  getMaxValuesPerformanceMatch,
  getMaxValuesPerformanceTraining,
  reversedVariables
} from '@/data/models/TableHeader'
import { GridItem, GridLayout } from 'vue-grid-layout'
import PerformanceGraphIndividual from '@/components/performance/PerformanceGraphIndividual.vue'
import PerformancePlayerInfoCard from '@/components/performance/PerformancePlayerInfoCard.vue'
import PerformanceStatCard from '@/components/performance/PerformanceStatCard.vue'
import { cloneDeep } from 'lodash'
import { getDateTimeFormatForUser, getUnitSuffixForUser, toCamelCase } from '@/utils/helpers'

export default {
  name: 'PerformanceIndividual',
  components: {
    PerformancePlayerInfoCard,
    PerformanceGraphIndividual,
    PerformanceStatCard,
    GridLayout,
    GridItem
  },
  props: {
    player: {
      type: Object,
      required: true
    },
    team: {
      type: Object,
      required: true
    },
    event: {
      type: Object,
      required: true
    },
    singlePlayerEvent: {
      type: Boolean,
      default: true
    },
    exercise: {
      type: Object,
      default: () => ({})
    },
    variables: {
      type: Array,
      default: () => []
    },
    layout: {
      type: Object,
      required: true
    },
    selectedExercise: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      period: {
        data: null,
        loading: false
      },
      exerciseStats: {
        data: [],
        loading: false
      },
      matchReferenceStats: {
        loading: false,
        data: []
      },
      layoutReady: false,
      selectedPeriodId: null,
      showPeriodAnnotations: false,
      showExerciseAnnotations: false,
      selectedMatchReferenceAggregation: { value: null, text: this.$t('button.values') }
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    performanceMaxValues() {
      return (this.event.data && this.event.data.event_type === 'training') ? this.colorMaxValuesTraining : this.colorMaxValuesMatch
    },
    playerMatchReferenceStats() {
      return this.matchReferenceStats.data.find(item => item.id === this.player.id) || {}
    },
    playerStats() {
      if (this.selectedMatchReferenceAggregation.value) {
        return this.playerMatchReferenceStats
      } else if (this.selectedExercise && this.exerciseStats.data && this.exerciseStats.data.players) {
        return this.exerciseStats.data?.players[0].player_stats || {}
      } else {
        return this.player.player_stats
      }
    },
    grid() {
      return cloneDeep(this.layout.grid)
    },
    colorMaxValuesTraining() {
      return getMaxValuesPerformanceTraining(this.user.organisation_settings?.unit_system)
    },
    colorMaxValuesMatch() {
      return getMaxValuesPerformanceMatch(this.user.organisation_settings?.unit_system)
    },
    matchReferenceAggregations() {
      return [
        { value: null, text: this.$t('button.values') },
        { value: 'avg_true', text: this.$t('button.avg_true') },
        { value: 'ponder_true', text: this.$t('button.ponder_true') },
        { value: 'avg_false', text: this.$t('button.avg_false') },
        { value: 'ponder_false', text: this.$t('button.ponder_false') }
      ]
    }
  },
  watch: {
    layout(val, oldVal) {
      if (val.id !== oldVal.id) {
        this.layoutReady = false
      }
    },
    selectedExercise: function (val) {
      if (val) {
        this.fetchStatsByExercise()
      }
    },
    selectedMatchReferenceAggregation() {
      if (this.selectedMatchReferenceAggregation.value) {
        this.fetchMatchReferenceStats()
      } else {
        this.matchReferenceStats.data = []
      }
    }
  },
  created() {
    this.fetchStatsByExercise()
  },
  methods: {
    getUnitSuffixForUser,
    getDateTimeFormatForUser,
    reversedVariables() {
      return reversedVariables
    },
    dayjs,
    toCamelCase,
    async fetchStatsByExercise() {
      if (!this.selectedExercise || !this.selectedExercise.internal_id) return

      if (this.exerciseStats.loading) return

      try {
        this.exerciseStats.loading = true

        const params = {
          player_ids: [this.player.id],
          event_ids: [this.event.data.id],
          training_protocol_exercise_ids: this.selectedExercise.type === 'superset' ? [this.selectedExercise.exercises[0].internal_id] : [this.selectedExercise.internal_id]
        }

        this.exerciseStats.data = await EventApiService.getAdvancedPerformanceStats(params)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_advanced_statistics'),
          color: 'error'
        })

        throw e
      } finally {
        this.exerciseStats.loading = false
      }
    },
    async fetchMatchReferenceStats() {
      this.matchReferenceStats.loading = true

      try {
        const data = {
          aggregation: this.selectedMatchReferenceAggregation.value.split('_')[0],
          full_game: this.selectedMatchReferenceAggregation.value.includes('true')
        }

        this.matchReferenceStats.data = await EventApiService.getMatchReferenceData(this.event.data.id, data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.match_reference_stats.error'),
          color: 'error'
        })
      } finally {
        this.matchReferenceStats.loading = false
      }
    },
    getVariableName(value) {
      return this.variables.find(variable => variable.value === value)?.long_text
    },
    getVariableSuffix(value) {
      return this.variables.find(variable => variable.value === value)?.suffix
    },
    layoutReadyEvent() {
      setTimeout(() => {
        this.layoutReady = true
      }, 100)
    }
  }
}
</script>
